<template>
  <model-form
    ref="modelForm"
    :model="reportElement"
    :modal-type="(reportElement.isLocal || editing) ? 'modal' : 'maximized-modal'"
    :width="editing ? '800px' : '500px'"
    :handled-actions="handledActions"
    @model-created="$emit('model-created')"
  >
    <div class="column">
      <div class="row q-gutter-md q-mb-md">
        <q-input
          v-model="reportElement.name"
          class="col"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <q-select
          v-model="reportElement.componentName"
          class="col-6"
          map-options
          :options="filteredReportComponentsAndSeparators"
          emit-value
          option-value="name"
          stack-label
          label="Component"
          use-input
          clearable
          @filter="filterReportComponents"
          @update:model-value="updateComponentName()"
        >
          <template #option="scope">
            <q-item
              v-if="scope.opt.isSeparator"
              :key="scope.opt.label"
              class="q-py-sm text-uppercase bg-grey-2 text-subtitle2"
              dense
            >
              {{ scope.opt.label }}
            </q-item>

            <q-item
              v-else
              v-bind="scope.itemProps"
              :key="scope.opt.key"
              class="constrain-width-q-item--lg"
              :class="$_.includes(filteredReportComponentsAndSeparators, scope.opt.value) ?
                'bg-grey-4 text-primary' : ''"
              v-on="scope.itemEvents"
            >
              <q-item-section
                avatar
              >
                <q-icon :name="scope.opt.icon" />
              </q-item-section>
              <q-item-section>
                <q-item-label v-if="scope.opt.category === 'Deprecated'">
                  <span><s>{{ scope.opt.name }}</s>
                    <q-icon
                      class="q-ml-xs"
                      name="error"
                      size="sm"
                    >
                      <q-tooltip>
                        This report element will be removed in the future and should no longer be used.
                      </q-tooltip>
                    </q-icon></span>
                </q-item-label>
                <q-item-section v-else>
                  <q-item-label>
                    {{ scope.opt.name }}
                  </q-item-label>
                  <q-item-label
                    v-if="scope.opt.description"
                    caption
                  >
                    {{ scope.opt.description }}
                  </q-item-label>
                </q-item-section>
              </q-item-section>
            </q-item>
          </template>
          <template #selected-item="scope">
            <q-item
              :key="scope.opt.key"
              class="q-pa-none q-ma-none"
              dense

              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section avatar>
                <q-icon :name="scope.opt.icon" />
              </q-item-section>
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>
    </div>
    <div
      v-if="reportElement.isRemote"
      class="column"
    >
      <report-controls
        :report-element="reportElement"
        :options-variables="['default', 'override', 'final']"
      />
    </div>
  </model-form>
</template>

<script>
import Report, { isOptionSet, optionNamesForControlName, setDefaultOption, unsetOption } from 'src/models/report/Report'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ReportControls from 'src/modules/admin/report/ReportControls'

export default {
  name: 'ReportElementForm',
  components: {
    ModelForm,
    ReportControls
  },
  props: {
    report: {
      type: Report,
      required: false,
      default: null
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    handledActions: {
      type: Array,
      required: false,
      default () {
        return ['add', 'edit']
      }
    }
  },
  emits: ['model-created'],
  data () {
    return {
      tab: 'default',
      componentSearchValue: null,
      settingControls: []
    }
  },
  configuration: ['reportComponents', 'regionReportComponents'],
  models: {
    reportElement: {
      controls: 'default',
      possibleOptionsValues: 'default'
    }
  },
  computed: {
    filteredReportComponentsAndSeparators () {
      const reportComponents = _.filter(
        this.report.regionReport ? this.regionReportComponents.data : this.reportComponents.data,
        component => component.name.toLowerCase().includes(this.componentSearchValue)
      )

      const options = _.map(reportComponents, component => ({
        name: component.name,
        icon: component.icon,
        label: component.label,
        description: component.description,
        category: component.category || 'Other',
        key: `option-${component.name}`
      }))

      const groupedByCategory = options.reduce((acc, item) => {
        const { category } = item
        if (!acc[category]) {
          acc[category] = []
        }
        acc[category].push(item)
        return acc
      }, {})

      const filteredReportComponentsAndSeparators = []

      // Move General category report elements to the top of the list
      if (groupedByCategory.General) {
        filteredReportComponentsAndSeparators.push({
          label: 'General',
          name: 'General',
          category: 'General',
          isSeparator: true,
          key: 'category-General'
        })
        filteredReportComponentsAndSeparators.push(...groupedByCategory.General)
      }

      Object.keys(groupedByCategory)
        .filter(category => category !== 'General' && category !== 'Deprecated')
        .sort()
        .forEach(category => {
          filteredReportComponentsAndSeparators.push({
            label: category,
            name: category,
            category,
            isSeparator: true,
            key: `category-${category}`
          })
          filteredReportComponentsAndSeparators.push(...groupedByCategory[category])
        })

      // Move deprecated report elements to the bottom of the list
      if (groupedByCategory.Deprecated) {
        filteredReportComponentsAndSeparators.push({
          label: 'Deprecated',
          name: 'Deprecated',
          category: 'Deprecated',
          isSeparator: true,
          key: 'category-Deprecated'
        })
        filteredReportComponentsAndSeparators.push(...groupedByCategory.Deprecated)
      }

      return filteredReportComponentsAndSeparators
    }
  },
  created () {
    _.extend(this, { optionNamesForControlName, isOptionSet, unsetOption })
  },
  methods: {
    updateComponentName () {
      this.reportElement.name = this.reportElement.componentName
    },
    show (model, initialValue) {
      this.$refs.modelForm.show(model, initialValue)
    },
    filterReportComponents (val, update, abort) {
      update(() => {
        this.componentSearchValue = val.toLowerCase()
      })
    },
    isOptionSetOrSetting (options, control) {
      return isOptionSet(options, control) || _.includes(this.settingControls, control)
    },
    toggleControl (options, control) {
      if (this.isOptionSetOrSetting(options, control)) {
        this.settingControls = _.without(this.settingControls, control)
        unsetOption(options, control)
      } else {
        this.settingControls = [...this.settingControls, control]
        setDefaultOption(options, this.reportElement.finalOptions, control)
      }
    }
  }
}

</script>
